import { useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  AppBar,
  Avatar,
  Box,
  ButtonBase,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Radio,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PeopleIcon from "@mui/icons-material/People";
import BusinessIcon from "@mui/icons-material/Business";
import DomainIcon from "@mui/icons-material/Domain";
import LocalAtmIcon from "@mui/icons-material/LocalAtm";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import TuneIcon from "@mui/icons-material/Tune";
import LabelIcon from "@mui/icons-material/Label";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import CampaignIcon from "@mui/icons-material/Campaign";
import HistoryIcon from "@mui/icons-material/History";
import GroupsIcon from "@mui/icons-material/Groups";
import InboxIcon from "@mui/icons-material/Inbox";
import WhatshotIcon from "@mui/icons-material/Whatshot";
import CallMadeIcon from "@mui/icons-material/CallMade";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import TableChartIcon from "@mui/icons-material/TableChart";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import WebIcon from "@mui/icons-material/Web";
import HubIcon from "@mui/icons-material/Hub";
import AppRegistrationIcon from "@mui/icons-material/AppRegistration";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import ChatIcon from "@mui/icons-material/Chat";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import GavelIcon from "@mui/icons-material/Gavel";
import BuildIcon from "@mui/icons-material/Build";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import BarChartIcon from "@mui/icons-material/BarChart";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import MergeTypeIcon from "@mui/icons-material/MergeType";
import IntegrationInstructionsIcon from "@mui/icons-material/IntegrationInstructions";
import WebhookIcon from "@mui/icons-material/Webhook";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import LightModeIcon from "@mui/icons-material/LightMode";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import SettingsBrightnessIcon from "@mui/icons-material/SettingsBrightness";
import { useAuth } from "../hooks/useAuth";
import { useHasRole } from "../hooks/useHasRole";
import { useAgentEvents } from "../hooks/useAgentEvents";
import { paths } from "../routes/paths";
import { NotificationBell } from "../features/notifications/NotificationBell";
import { useThemeMode, type ThemePreference } from "../theme/ThemeModeProvider";
import { ChatRailProvider, useChatRail } from "../features/agents/ChatRail/ChatRailProvider";
import { ChatRail } from "../features/agents/ChatRail/ChatRail";
import { ChatRailMobile } from "../features/agents/ChatRail/ChatRailMobile";
import { RelatedRail } from "./RelatedRail";
import { MfaSessionHint } from "./MfaSessionHint";
import { useRelatedRailWidth, useRelatedRailCollapsed } from "../hooks/useRelatedRailWidth";
import { useLeftNavCollapsed } from "../hooks/useLeftNavCollapsed";
import { useFocusModeShortcut } from "../hooks/useFocusModeShortcut";
import { useCommandPaletteShortcut } from "../hooks/useCommandPaletteShortcut";
import { useNavigationShortcuts } from "../hooks/useNavigationShortcuts";
import { CommandPalette } from "../features/search/CommandPalette";
import { KeyboardCheatSheet } from "./KeyboardCheatSheet";
import { useCheatSheetShortcut } from "../hooks/useCheatSheetShortcut";
import { HelpDrawerProvider, HelpButton } from "./HelpDrawer";
import { SampleDataPrompt } from "./SampleDataPrompt";

const RELATED_COLLAPSED_W = 48;
import { useDetailRoute } from "../hooks/useDetailRoute";

const DRAWER_WIDTH = 240;
const DRAWER_COLLAPSED_WIDTH = 56;

const mainNavItems = [
  { label: "Dashboard", path: paths.dashboard, icon: <DashboardIcon /> },
  { label: "Contacts", path: paths.contacts, icon: <PeopleIcon /> },
  { label: "Organizations", path: paths.organizations, icon: <DomainIcon /> },
  { label: "Deals", path: paths.deals, icon: <LocalAtmIcon /> },
  { label: "Activities", path: paths.activities, icon: <TaskAltIcon /> },
  { label: "Prospects", path: paths.prospects, icon: <WhatshotIcon /> },
  { label: "Triage", path: paths.triage, icon: <InboxIcon /> },
  { label: "Partners", path: paths.partners, icon: <HubIcon /> },
];

// Reviewer-only main-nav items (admin / revenue_leader)
const reviewerNavItems = [
  {
    // Hub: partner-submitted registrations awaiting approval + deals already
    // registered to a partner. (Absorbed the former "Partner Submissions" item
    // in the 2026-06-08 merge.)
    label: "Registrations",
    path: paths.dealRegistrations,
    icon: <AppRegistrationIcon />,
  },
  {
    // Outbound: customers/deals we've referred to partners.
    label: "Referrals",
    path: paths.referrals,
    icon: <CallMadeIcon />,
  },
  {
    label: "Forecast",
    path: paths.forecast,
    icon: <BarChartIcon />,
  },
];

const agenticNavItems = [
  { label: "Agents", path: paths.agents, icon: <SmartToyIcon fontSize="small" /> },
  { label: "Templates", path: paths.templates, icon: <AutoAwesomeIcon fontSize="small" /> },
  { label: "Runs", path: paths.runs, icon: <PlayCircleIcon fontSize="small" /> },
  { label: "Approvals", path: paths.approvals, icon: <GavelIcon fontSize="small" /> },
];

// Admin-only inside the agentic section.
const agenticAdminNavItems = [
  { label: "Skills", path: paths.skills, icon: <BuildIcon fontSize="small" /> },
  {
    label: "Conversations (audit)",
    path: paths.conversations,
    icon: <ChatIcon fontSize="small" />,
  },
];

const marketingNavItems = [
  {
    label: "Campaigns",
    path: paths.marketingCampaigns,
    icon: <CampaignIcon fontSize="small" />,
  },
  {
    label: "Landing Pages",
    path: paths.marketingLandingPages,
    icon: <WebIcon fontSize="small" />,
  },
  {
    label: "Account Lists",
    path: paths.marketingAccountLists,
    icon: <PlaylistAddCheckIcon fontSize="small" />,
  },
];

const adminNavItems = [
  { label: "Users", path: paths.adminUsers, icon: <ManageAccountsIcon fontSize="small" /> },
  { label: "Roles", path: paths.adminRoles, icon: <GroupsIcon fontSize="small" /> },
  { label: "Pipelines", path: paths.adminPipelines, icon: <TuneIcon fontSize="small" /> },
  { label: "Lead Sources", path: paths.adminLeadSources, icon: <LabelIcon fontSize="small" /> },
  { label: "Tags", path: paths.adminTags, icon: <LocalOfferIcon fontSize="small" /> },
  {
    label: "Territories",
    path: paths.adminTerritories,
    icon: <AccountTreeIcon fontSize="small" />,
  },
  {
    label: "Partner Companies",
    path: paths.adminPartnerCompanies,
    icon: <BusinessIcon fontSize="small" />,
  },
  {
    label: "Reference Tables",
    path: paths.adminReferenceTables,
    icon: <TableChartIcon fontSize="small" />,
  },
  { label: "Audit Log", path: paths.adminAuditLog, icon: <HistoryIcon fontSize="small" /> },
  { label: "Import", path: paths.adminImport, icon: <UploadFileIcon fontSize="small" /> },
  {
    label: "Duplicates",
    path: paths.adminDuplicatesContacts,
    icon: <MergeTypeIcon fontSize="small" />,
  },
  {
    label: "Integrations",
    path: paths.adminIntegrations,
    icon: <IntegrationInstructionsIcon fontSize="small" />,
  },
  {
    label: "Teams Webhooks",
    path: paths.adminTeamsWebhooks,
    icon: <WebhookIcon fontSize="small" />,
  },
  {
    label: "Commissions",
    path: paths.adminCommissions,
    icon: <LocalAtmIcon fontSize="small" />,
  },
];

export function Layout() {
  return (
    <ChatRailProvider>
      <HelpDrawerProvider>
        <LayoutInner />
      </HelpDrawerProvider>
    </ChatRailProvider>
  );
}

function LayoutInner() {
  const { collapsed: leftNavCollapsed, setCollapsed: setLeftNavCollapsed } = useLeftNavCollapsed();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [marketingOpen, setMarketingOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [agenticOpen, setAgenticOpen] = useState(false);
  const [userMenuEl, setUserMenuEl] = useState<null | HTMLElement>(null);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [cheatSheetOpen, setCheatSheetOpen] = useState(false);
  const { user, signOut } = useAuth();
  const { preference: themePref, setPreference: setThemePref } = useThemeMode();
  useAgentEvents();
  const { available: railAvailable, collapsed: railCollapsed, widthPx: railWidth } = useChatRail();
  const railOffset = railAvailable ? (railCollapsed ? 56 : railWidth) : 0;
  const detailCtx = useDetailRoute();
  const { widthPx: relatedRailWidth } = useRelatedRailWidth();
  const { collapsed: relatedCollapsed, setCollapsed: setRelatedCollapsed } =
    useRelatedRailCollapsed();
  const { setCollapsed: setChatCollapsed } = useChatRail();
  const relatedRailOffset = detailCtx
    ? relatedCollapsed
      ? RELATED_COLLAPSED_W
      : relatedRailWidth
    : 0;

  useFocusModeShortcut({
    leftNavCollapsed,
    setLeftNavCollapsed,
    relatedCollapsed,
    setRelatedCollapsed,
    chatCollapsed: railCollapsed,
    setChatCollapsed,
  });
  useCommandPaletteShortcut(() => setPaletteOpen((v) => !v));
  useCheatSheetShortcut(() => setCheatSheetOpen((v) => !v));
  useNavigationShortcuts();
  const isAdmin = useHasRole("manna", "admin");
  const isRevLeader = useHasRole("manna", "revenue_leader");
  const isMarketingManager = useHasRole("manna", "marketing_manager");
  const showAdmin = isAdmin || isRevLeader;
  const showMarketing = showAdmin || isMarketingManager;
  const navigate = useNavigate();
  const location = useLocation();

  // Keep the admin section open whenever the current route is under /admin
  const adminSectionOpen = adminOpen || location.pathname.startsWith(paths.admin);
  // Same pattern for marketing
  const marketingSectionOpen = marketingOpen || location.pathname.startsWith(paths.marketing);
  // Open agentic section when on any agentic route
  const agenticSectionOpen =
    agenticOpen ||
    location.pathname.startsWith(paths.agents) ||
    location.pathname.startsWith(paths.templates) ||
    location.pathname.startsWith(paths.conversations) ||
    location.pathname.startsWith(paths.runs) ||
    location.pathname.startsWith(paths.approvals) ||
    location.pathname.startsWith(paths.skills);

  const drawerWidth = leftNavCollapsed ? DRAWER_COLLAPSED_WIDTH : DRAWER_WIDTH;

  const drawerContent = (collapsed: boolean) => (
    <Box
      sx={{
        width: collapsed ? DRAWER_COLLAPSED_WIDTH : DRAWER_WIDTH,
        overflowX: "hidden",
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      {/* LeftNav header — squircle logo + Manna wordmark + ⌘K hint
          (prototype proto-crm.jsx:43-56). When collapsed, only the
          squircle is shown. */}
      <Toolbar
        disableGutters
        sx={{
          justifyContent: collapsed ? "center" : "space-between",
          px: collapsed ? 0 : 1.75,
          gap: 1,
          minHeight: 56,
          flexDirection: collapsed ? "column" : "row",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
          <Box
            sx={{
              width: 26,
              height: 26,
              borderRadius: "6px",
              bgcolor: "text.primary",
              color: "background.paper",
              display: "grid",
              placeItems: "center",
              fontSize: 14,
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            M
          </Box>
          {!collapsed && (
            <Typography sx={{ fontSize: 14, fontWeight: 600, letterSpacing: -0.1 }} noWrap>
              Manna
            </Typography>
          )}
        </Box>
        {!collapsed && (
          <Tooltip title="Search (⌘K)" placement="bottom">
            <ButtonBase
              onClick={() => setPaletteOpen(true)}
              aria-label="Open command palette"
              sx={{
                fontFamily: (t) => t.typography.overline.fontFamily,
                fontSize: 11,
                color: "text.secondary",
                border: (t) => `1px solid ${t.palette.divider}`,
                borderRadius: "5px",
                px: 0.75,
                py: 0.25,
                letterSpacing: 0.4,
                "&:hover": { bgcolor: "action.hover", color: "text.primary" },
              }}
            >
              ⌘K
            </ButtonBase>
          </Tooltip>
        )}
        {/* Collapse/expand handled by the always-visible edge toggle on the
            drawer's right border (see desktop Drawer below). */}
      </Toolbar>

      <List component="nav" disablePadding sx={{ flex: 1, overflowY: "auto" }} aria-label="Primary">
        {/* Main nav items */}
        {mainNavItems.map((item) => (
          <ListItem key={item.path} disablePadding component="div" sx={{ display: "block" }}>
            <Tooltip title={collapsed ? item.label : ""} placement="right">
              <ListItemButton
                selected={location.pathname.startsWith(item.path)}
                onClick={() => {
                  navigate(item.path);
                  setMobileOpen(false);
                }}
                sx={{
                  justifyContent: collapsed ? "center" : "flex-start",
                  px: collapsed ? 1.5 : 2,
                }}
              >
                <ListItemIcon sx={{ minWidth: collapsed ? 0 : 40, justifyContent: "center" }}>
                  {item.icon}
                </ListItemIcon>
                {!collapsed && <ListItemText primary={item.label} />}
              </ListItemButton>
            </Tooltip>
          </ListItem>
        ))}

        {/* Reviewer-only items (admin / revenue_leader) */}
        {showAdmin &&
          reviewerNavItems.map((item) => (
            <ListItem key={item.path} disablePadding component="div" sx={{ display: "block" }}>
              <Tooltip title={collapsed ? item.label : ""} placement="right">
                <ListItemButton
                  selected={location.pathname === item.path}
                  onClick={() => {
                    navigate(item.path);
                    setMobileOpen(false);
                  }}
                  sx={{
                    justifyContent: collapsed ? "center" : "flex-start",
                    px: collapsed ? 1.5 : 2,
                  }}
                >
                  <ListItemIcon sx={{ minWidth: collapsed ? 0 : 40, justifyContent: "center" }}>
                    {item.icon}
                  </ListItemIcon>
                  {!collapsed && <ListItemText primary={item.label} />}
                </ListItemButton>
              </Tooltip>
            </ListItem>
          ))}

        {/* Agentic platform section — admin / revenue_leader only */}
        {showAdmin && (
          <>
            {!collapsed ? (
              <SectionHeader
                label="Agentic"
                open={agenticSectionOpen}
                onToggle={() => setAgenticOpen((v) => !v)}
              />
            ) : (
              <Tooltip title="Agents" placement="right">
                <ListItemButton
                  onClick={() => {
                    setLeftNavCollapsed(false);
                    navigate(paths.agents);
                  }}
                  sx={{ justifyContent: "center", px: 1.5 }}
                >
                  <ListItemIcon sx={{ minWidth: 0, justifyContent: "center" }}>
                    <SmartToyIcon />
                  </ListItemIcon>
                </ListItemButton>
              </Tooltip>
            )}

            {!collapsed && (
              <Collapse in={agenticSectionOpen} timeout="auto">
                <List component="div" disablePadding>
                  {agenticNavItems.map((item) => (
                    <ListItemButton
                      key={item.path}
                      selected={location.pathname === item.path}
                      onClick={() => {
                        navigate(item.path);
                        setMobileOpen(false);
                      }}
                      sx={{ pl: 2, py: 0.5 }}
                    >
                      <ListItemIcon sx={{ minWidth: 32 }}>{item.icon}</ListItemIcon>
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{ variant: "body2" }}
                      />
                    </ListItemButton>
                  ))}
                  {agenticAdminNavItems.map((item) => (
                    <ListItemButton
                      key={item.path}
                      selected={location.pathname === item.path}
                      onClick={() => {
                        navigate(item.path);
                        setMobileOpen(false);
                      }}
                      sx={{ pl: 2, py: 0.5 }}
                    >
                      <ListItemIcon sx={{ minWidth: 32 }}>{item.icon}</ListItemIcon>
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{ variant: "body2" }}
                      />
                    </ListItemButton>
                  ))}
                </List>
              </Collapse>
            )}
          </>
        )}

        {/* Marketing section — admin / revenue_leader / marketing_manager */}
        {showMarketing && (
          <>
            {!collapsed ? (
              <SectionHeader
                label="Marketing"
                open={marketingSectionOpen}
                onToggle={() => setMarketingOpen((v) => !v)}
              />
            ) : (
              <Tooltip title="Marketing" placement="right">
                <ListItemButton
                  onClick={() => {
                    setLeftNavCollapsed(false);
                    navigate(paths.marketingCampaigns);
                  }}
                  sx={{ justifyContent: "center", px: 1.5 }}
                >
                  <ListItemIcon sx={{ minWidth: 0, justifyContent: "center" }}>
                    <RocketLaunchIcon />
                  </ListItemIcon>
                </ListItemButton>
              </Tooltip>
            )}

            {!collapsed && (
              <Collapse in={marketingSectionOpen} timeout="auto">
                <List component="div" disablePadding>
                  {marketingNavItems.map((item) => (
                    <ListItemButton
                      key={item.path}
                      selected={location.pathname === item.path}
                      onClick={() => {
                        navigate(item.path);
                        setMobileOpen(false);
                      }}
                      sx={{ pl: 2, py: 0.5 }}
                    >
                      <ListItemIcon sx={{ minWidth: 32 }}>{item.icon}</ListItemIcon>
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{ variant: "body2" }}
                      />
                    </ListItemButton>
                  ))}
                </List>
              </Collapse>
            )}
          </>
        )}

        {/* Admin section */}
        {showAdmin && (
          <>
            {!collapsed ? (
              <SectionHeader
                label="Admin"
                open={adminSectionOpen}
                onToggle={() => setAdminOpen((v) => !v)}
              />
            ) : (
              <Tooltip title="Admin" placement="right">
                <ListItemButton
                  onClick={() => {
                    setLeftNavCollapsed(false);
                    navigate(paths.adminUsers);
                  }}
                  sx={{ justifyContent: "center", px: 1.5 }}
                >
                  <ListItemIcon sx={{ minWidth: 0, justifyContent: "center" }}>
                    <ManageAccountsIcon />
                  </ListItemIcon>
                </ListItemButton>
              </Tooltip>
            )}

            {!collapsed && (
              <Collapse in={adminSectionOpen} timeout="auto">
                <List component="div" disablePadding>
                  {adminNavItems.map((item) => (
                    <ListItemButton
                      key={item.path}
                      selected={location.pathname === item.path}
                      onClick={() => {
                        navigate(item.path);
                        setMobileOpen(false);
                      }}
                      sx={{ pl: 2, py: 0.5 }}
                    >
                      <ListItemIcon sx={{ minWidth: 32 }}>{item.icon}</ListItemIcon>
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{ variant: "body2" }}
                      />
                    </ListItemButton>
                  ))}
                </List>
              </Collapse>
            )}
          </>
        )}
      </List>

      {/* Collapse/expand toggle — quiet, in-pane, at the bottom (matches the
          other panel collapsers; desktop only). */}
      <Tooltip title={collapsed ? "Expand navigation" : ""} placement="right">
        <ButtonBase
          onClick={() => setLeftNavCollapsed(!collapsed)}
          aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
          sx={{
            display: { xs: "none", md: "flex" },
            width: "100%",
            px: collapsed ? 0 : 2,
            py: 1,
            gap: 1,
            justifyContent: collapsed ? "center" : "flex-start",
            color: "text.secondary",
            borderTop: (t) => `1px solid ${t.palette.divider}`,
            "&:hover": { bgcolor: "action.hover", color: "text.primary" },
          }}
        >
          {collapsed ? (
            <ChevronRightIcon fontSize="small" />
          ) : (
            <>
              <ChevronLeftIcon fontSize="small" />
              <Typography sx={{ fontSize: 13 }}>Collapse</Typography>
            </>
          )}
        </ButtonBase>
      </Tooltip>

      {/* LeftNav footer — user pill (avatar + email + org), opens user
          menu on click. Prototype proto-crm.jsx:82-95. */}
      <ButtonBase
        onClick={(e) => setUserMenuEl(e.currentTarget)}
        sx={{
          width: "100%",
          px: collapsed ? 0 : 1.5,
          py: 1.25,
          borderTop: (t) => `1px solid ${t.palette.divider}`,
          gap: 1.25,
          justifyContent: collapsed ? "center" : "flex-start",
          "&:hover": { bgcolor: "action.hover" },
        }}
      >
        <Avatar
          sx={{
            width: 26,
            height: 26,
            fontSize: 11,
            fontWeight: 600,
            bgcolor: "primary.main",
            color: "primary.contrastText",
          }}
        >
          {user?.email?.[0]?.toUpperCase() ?? "?"}
        </Avatar>
        {!collapsed && (
          <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0, textAlign: "left" }}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 500, lineHeight: 1.2 }} noWrap>
              {user?.email ?? "—"}
            </Typography>
            <Typography
              sx={{
                fontSize: 10.5,
                fontFamily: (t) => t.typography.overline.fontFamily,
                color: "text.secondary",
                letterSpacing: 0.3,
                lineHeight: 1.2,
              }}
              noWrap
            >
              Emgage
            </Typography>
          </Box>
        )}
      </ButtonBase>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <AppBar position="fixed" sx={{ zIndex: (t) => t.zIndex.drawer + 1 }}>
        <Toolbar sx={{ minHeight: 56 }}>
          <IconButton
            color="inherit"
            edge="start"
            onClick={() => setMobileOpen(true)}
            aria-label="Open navigation menu"
            sx={{ mr: 1, display: { md: "none" } }}
          >
            <MenuIcon />
          </IconButton>
          {leftNavCollapsed && (
            <IconButton
              color="inherit"
              edge="start"
              onClick={() => setLeftNavCollapsed(false)}
              aria-label="Expand navigation"
              sx={{ mr: 1, display: { xs: "none", md: "flex" } }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <HelpButton />
          <NotificationBell />
        </Toolbar>
      </AppBar>

      <Menu
        anchorEl={userMenuEl}
        open={Boolean(userMenuEl)}
        onClose={() => setUserMenuEl(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <MenuItem disabled>{user?.email}</MenuItem>
        <MenuItem
          onClick={() => {
            setUserMenuEl(null);
            navigate(paths.meProfile);
          }}
        >
          Profile
        </MenuItem>
        <MenuItem
          onClick={() => {
            setUserMenuEl(null);
            navigate(paths.meNotifications);
          }}
        >
          Notifications
        </MenuItem>
        <MenuItem
          onClick={() => {
            setUserMenuEl(null);
            navigate(paths.security);
          }}
        >
          Security
        </MenuItem>
        <Divider />
        <Typography variant="caption" sx={{ px: 2, color: "text.secondary" }}>
          Appearance
        </Typography>
        {(
          [
            { key: "light", label: "Light Mode", icon: <LightModeIcon fontSize="small" /> },
            { key: "dark", label: "Dark Mode", icon: <DarkModeIcon fontSize="small" /> },
            {
              key: "system",
              label: "System",
              icon: <SettingsBrightnessIcon fontSize="small" />,
            },
          ] as { key: ThemePreference; label: string; icon: React.ReactNode }[]
        ).map((opt) => (
          <MenuItem
            key={opt.key}
            onClick={() => setThemePref(opt.key)}
            selected={themePref === opt.key}
          >
            <Radio checked={themePref === opt.key} size="small" sx={{ mr: 1, p: 0.5 }} />
            <ListItemIcon sx={{ minWidth: 32 }}>{opt.icon}</ListItemIcon>
            <ListItemText primary={opt.label} />
          </MenuItem>
        ))}
        <Divider />
        <MenuItem
          onClick={async () => {
            setUserMenuEl(null);
            await signOut();
          }}
        >
          Sign out
        </MenuItem>
      </Menu>

      {/* Desktop permanent collapsible drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: "none", md: "block" },
          width: drawerWidth,
          flexShrink: 0,
          whiteSpace: "nowrap",
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
            overflowX: "hidden",
            transition: (t) =>
              t.transitions.create("width", {
                easing: t.transitions.easing.sharp,
                duration: leftNavCollapsed
                  ? t.transitions.duration.leavingScreen
                  : t.transitions.duration.enteringScreen,
              }),
          },
        }}
      >
        {drawerContent(leftNavCollapsed)}
      </Drawer>

      {/* Mobile temporary drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": { width: DRAWER_WIDTH },
        }}
      >
        {drawerContent(false)}
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, md: 3 },
          mt: "64px",
          ml: { md: 0 },
          mr: { xs: 0, md: `${railOffset + relatedRailOffset}px` },
          minWidth: 0,
          transition: "margin-right 180ms cubic-bezier(0.22, 0.61, 0.36, 1)",
        }}
      >
        <MfaSessionHint />
        <Outlet />
      </Box>

      {detailCtx && (
        <Box
          sx={{
            display: { xs: "none", md: "block" },
            position: "fixed",
            top: 0,
            right: `${railOffset}px`,
            mt: "64px",
            transition: "right 180ms cubic-bezier(0.22, 0.61, 0.36, 1)",
          }}
        >
          <RelatedRail ctx={detailCtx} />
        </Box>
      )}

      <Box sx={{ display: { xs: "none", md: "block" } }}>
        <ChatRail />
      </Box>
      <Box sx={{ display: { xs: "block", md: "none" } }}>
        <ChatRailMobile />
      </Box>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
      <KeyboardCheatSheet open={cheatSheetOpen} onClose={() => setCheatSheetOpen(false)} />
      <SampleDataPrompt />
    </Box>
  );
}

// Quiet section header — mono uppercase label + chevron, no leading icon.
// Visual reference: prototype proto-crm.jsx:60-63 ("WORKSPACE"/"VIEWS"
// overline labels). Manna keeps the click-to-collapse affordance because
// admin-heavy nav doesn't fit a flat list.
function SectionHeader({
  label,
  open,
  onToggle,
}: {
  label: string;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <ButtonBase
      onClick={onToggle}
      sx={{
        width: "100%",
        px: 1,
        pt: 2,
        pb: 0.75,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        "&:hover": { bgcolor: "transparent", "& .section-chevron": { color: "text.primary" } },
      }}
    >
      <Typography
        sx={{
          fontFamily: (t) => t.typography.overline.fontFamily,
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: 1.4,
          color: "text.secondary",
          textTransform: "uppercase",
        }}
      >
        {label}
      </Typography>
      {open ? (
        <ExpandLessIcon className="section-chevron" sx={{ fontSize: 14, color: "text.disabled" }} />
      ) : (
        <ExpandMoreIcon className="section-chevron" sx={{ fontSize: 14, color: "text.disabled" }} />
      )}
    </ButtonBase>
  );
}
